/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "@core/lib/supabase";
import { logger } from "@core/lib/logger";
import { ErrorFactory, logError } from "@core/lib/error-handling";

interface Product {
  id: string;
  title: string;
  description?: string;
  type: "date" | "form" | "quizz";
  status: "active" | "archived" | "deleted";
  created_at: string;
  updated_at: string;
  responseCount?: number;
  is_favorite?: boolean;
}

interface ProductState {
  products: Product[];
  loading: boolean;
  error: Error | null;
  selectedProduct: Product | null;
  filters: {
    type?: "date" | "form" | "quizz";
    status?: "active" | "archived" | "deleted";
    search?: string;
    favorites?: boolean;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

type ProductAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: Error | null }
  | { type: "SET_PRODUCTS"; payload: Product[] }
  | { type: "ADD_PRODUCT"; payload: Product }
  | {
      type: "UPDATE_PRODUCT";
      payload: { id: string; updates: Partial<Product> };
    }
  | { type: "DELETE_PRODUCT"; payload: string }
  | { type: "SET_SELECTED_PRODUCT"; payload: Product | null }
  | { type: "SET_FILTERS"; payload: Partial<ProductState["filters"]> }
  | {
      type: "SET_FILTERS_AND_RESET_PAGE";
      payload: Partial<ProductState["filters"]>;
    }
  | { type: "SET_PAGINATION"; payload: Partial<ProductState["pagination"]> }
  | { type: "TOGGLE_FAVORITE"; payload: { id: string; is_favorite: boolean } }
  | { type: "RESET_STATE" };

const initialState: ProductState = {
  products: [],
  loading: false,
  error: null,
  selectedProduct: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
  },
};

function productReducer(
  state: ProductState,
  action: ProductAction,
): ProductState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };

    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false };

    case "SET_PRODUCTS":
      return { ...state, products: action.payload, loading: false };

    case "ADD_PRODUCT":
      return {
        ...state,
        products: [action.payload, ...state.products],
        loading: false,
      };

    case "UPDATE_PRODUCT":
      return {
        ...state,
        products: state.products.map((product) =>
          product.id === action.payload.id
            ? { ...product, ...action.payload.updates }
            : product,
        ),
        selectedProduct:
          state.selectedProduct?.id === action.payload.id
            ? { ...state.selectedProduct, ...action.payload.updates }
            : state.selectedProduct,
        loading: false,
      };

    case "DELETE_PRODUCT":
      return {
        ...state,
        products: state.products.filter(
          (product) => product.id !== action.payload,
        ),
        selectedProduct:
          state.selectedProduct?.id === action.payload
            ? null
            : state.selectedProduct,
        loading: false,
      };

    case "SET_SELECTED_PRODUCT":
      return { ...state, selectedProduct: action.payload };

    case "SET_FILTERS":
      return { ...state, filters: { ...state.filters, ...action.payload } };

    case "SET_FILTERS_AND_RESET_PAGE":
      // Bug #8 — un seul dispatch atomique pour éviter deux re-runs
      // de l'effet de chargement (filters puis pagination.page).
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        pagination: { ...state.pagination, page: 1 },
      };

    case "SET_PAGINATION":
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload },
      };

    case "TOGGLE_FAVORITE":
      return {
        ...state,
        products: state.products.map((product) =>
          product.id === action.payload.id
            ? { ...product, is_favorite: action.payload.is_favorite }
            : product,
        ),
        selectedProduct:
          state.selectedProduct?.id === action.payload.id
            ? {
                ...state.selectedProduct,
                is_favorite: action.payload.is_favorite,
              }
            : state.selectedProduct,
      };

    case "RESET_STATE":
      return initialState;

    default:
      return state;
  }
}

interface ProductContextType {
  state: ProductState;
  actions: {
    loadProducts: () => Promise<void>;
    createProduct: (
      data: Omit<Product, "id" | "created_at" | "updated_at">,
    ) => Promise<Product>;
    updateProduct: (id: string, updates: Partial<Product>) => Promise<void>;
    deleteProduct: (id: string) => Promise<void>;
    selectProduct: (product: Product | null) => void;
    setFilters: (filters: Partial<ProductState["filters"]>) => void;
    setPagination: (pagination: Partial<ProductState["pagination"]>) => void;
    refreshProducts: () => Promise<void>;
    toggleFavorite: (id: string, isFavorite: boolean) => Promise<void>;
  };
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

interface ProductProviderProps {
  children: ReactNode;
}

export const ProductProvider: React.FC<ProductProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(productReducer, initialState);
  const { user } = useAuth();

  const loadProducts = async () => {
    if (!user) return;

    dispatch({ type: "SET_LOADING", payload: true });
    dispatch({ type: "SET_ERROR", payload: null });

    try {
      let query = supabase.from("products").select("*", { count: "exact" });

      if (state.filters.type) query = query.eq("type", state.filters.type);
      if (state.filters.status)
        query = query.eq("status", state.filters.status);
      if (state.filters.search)
        query = query.ilike("title", `%${state.filters.search}%`);
      if (state.filters.favorites) query = query.eq("is_favorite", true);

      // Pagination
      const from = (state.pagination.page - 1) * state.pagination.limit;
      const to = from + state.pagination.limit - 1;
      query = query.range(from, to).order("updated_at", { ascending: false });

      const { data, error, count } = await query;

      if (error) throw error;

      dispatch({ type: "SET_PRODUCTS", payload: (data as Product[]) || [] });
      if (count !== null) {
        dispatch({ type: "SET_PAGINATION", payload: { total: count } });
      }
    } catch (error) {
      dispatch({
        type: "SET_ERROR",
        payload: error instanceof Error ? error : new Error("Erreur inconnue"),
      });
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: "ProductContext",
        operation: "loadProducts",
      });
    }
  };

  const createProduct = async (
    data: Omit<Product, "id" | "created_at" | "updated_at">,
  ) => {
    if (!user)
      throw ErrorFactory.auth(
        "Utilisateur non authentifié",
        "Vous devez être connecté pour créer un produit",
      );

    try {
      // ✅ Utilisation directe de Supabase
      const { data: product, error } = await supabase
        .from("products")
        .insert([{ ...data, creator_id: user.id }])
        .select()
        .single();

      if (error) throw error;

      dispatch({ type: "ADD_PRODUCT", payload: product as Product });
      return product as Product;
    } catch (error) {
      dispatch({
        type: "SET_ERROR",
        payload: error instanceof Error ? error : new Error("Erreur inconnue"),
      });
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: "ProductContext",
        operation: "createProduct",
      });
      throw error;
    }
  };

  const updateProduct = async (id: string, updates: Partial<Product>) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("products")
        .update(updates)
        .eq("id", id);

      if (error) throw error;

      dispatch({ type: "UPDATE_PRODUCT", payload: { id, updates } });
    } catch (error) {
      dispatch({
        type: "SET_ERROR",
        payload: error instanceof Error ? error : new Error("Erreur inconnue"),
      });
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: "ProductContext",
        operation: "updateProduct",
      });
      throw error;
    }
  };

  const deleteProduct = async (id: string) => {
    if (!user) return;

    try {
      const { error } = await supabase.from("products").delete().eq("id", id);

      if (error) throw error;

      dispatch({ type: "DELETE_PRODUCT", payload: id });
    } catch (error) {
      dispatch({
        type: "SET_ERROR",
        payload: error instanceof Error ? error : new Error("Erreur inconnue"),
      });
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: "ProductContext",
        operation: "deleteProduct",
      });
      throw error;
    }
  };

  const selectProduct = (product: Product | null) => {
    dispatch({ type: "SET_SELECTED_PRODUCT", payload: product });
  };

  const setFilters = (filters: Partial<ProductState["filters"]>) => {
    // Bug #8 — un seul dispatch atomique au lieu de deux consécutifs
    dispatch({ type: "SET_FILTERS_AND_RESET_PAGE", payload: filters });
  };

  const setPagination = (pagination: Partial<ProductState["pagination"]>) => {
    dispatch({ type: "SET_PAGINATION", payload: pagination });
  };

  const refreshProducts = async () => {
    await loadProducts();
  };

  const toggleFavorite = async (id: string, isFavorite: boolean) => {
    if (!user) return;

    // Optimistic update
    dispatch({
      type: "TOGGLE_FAVORITE",
      payload: { id, is_favorite: isFavorite },
    });

    try {
      // Using direct Supabase update via API wrapper if possible, or fallback to patch
      // Since we don't have a specific endpoint for toggle, we use patch
      // await api.patch(`/products/${id}`, { is_favorite: isFavorite });
      const { error } = await supabase
        .from("products")
        .update({ is_favorite: isFavorite })
        .eq("id", id);

      if (error) throw error;
    } catch (error) {
      // Revert on error
      dispatch({
        type: "TOGGLE_FAVORITE",
        payload: { id, is_favorite: !isFavorite },
      });

      dispatch({
        type: "SET_ERROR",
        payload: error instanceof Error ? error : new Error("Erreur inconnue"),
      });
      logError(error instanceof Error ? error : new Error(String(error)), {
        component: "ProductContext",
        operation: "toggleFavorite",
      });
    }
  };

  // Charger les produits au montage et quand les filtres changent.
  // Bug #8 — dépendre de `user?.id` (primitif) plutôt que de `user` (objet)
  // pour éviter un re-fetch à chaque event onAuthStateChange (INITIAL_SESSION
  // puis SIGNED_IN livrent un nouvel objet user mais même id).
  useEffect(() => {
    if (user) {
      loadProducts();
    }
  }, [user?.id, state.filters, state.pagination.page, state.pagination.limit]);

  const value: ProductContextType = {
    state,
    actions: {
      loadProducts,
      createProduct,
      updateProduct,
      deleteProduct,
      selectProduct,
      setFilters,
      setPagination,
      refreshProducts,
      toggleFavorite,
    },
  };

  return (
    <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
  );
};

export const useProductContext = (): ProductContextType => {
  const context = useContext(ProductContext);

  // Ajouter un guard pour éviter l'erreur pendant les renders
  if (context === undefined) {
    console.warn(
      "🔍 useProductContext called outside ProductProvider - returning default context",
    );
    // Retourner un contexte par défaut pour éviter l'erreur
    return {
      state: {
        products: [],
        loading: false,
        error: null,
        selectedProduct: null,
        filters: {
          type: null,
          status: null,
          search: "",
        },
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
        },
      },
      actions: {
        loadProducts: async () => {},
        createProduct: async () => null,
        updateProduct: async () => {},
        deleteProduct: async () => {},
        selectProduct: () => {},
        setFilters: () => {},
        setPagination: () => {},
        refreshProducts: async () => {},
        toggleFavorite: async () => {},
      },
    };
  }

  return context;
};
